import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CASE_STUDIES } from "../mock/mock";
import {
  CASE_STUDY_AUTO_ADVANCE_MS,
  CASE_STUDY_TRANSITION_OUT_MS,
} from "../lib/constants";

const CaseStudies = () => {
  const [active, setActive] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const [shown, setShown] = useState(false);

  const total = CASE_STUDIES.length;
  const current = CASE_STUDIES[active];

  // Section reveal — mount-once IntersectionObserver
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setShown(true), obs.disconnect()),
      { threshold: 0.1 }
    );
    const node = sectionRef.current;
    if (node) obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const goTo = useCallback(
    (i) => {
      setActive((prev) => {
        if (i === prev) return prev;
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), CASE_STUDY_TRANSITION_OUT_MS);
        return (i + total) % total;
      });
    },
    [total]
  );

  // Auto advance — pauses on hover, retriggers when active changes
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(
      () => goTo((active + 1) % total),
      CASE_STUDY_AUTO_ADVANCE_MS
    );
    return () => clearInterval(t);
  }, [active, paused, total, goTo]);

  const next = () => goTo((active + 1) % total);
  const prev = () => goTo((active - 1 + total) % total);

  return (
    <section
      ref={sectionRef}
      className="relative bg-black text-white overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Subtle background tone — keep section solid like OUTFRONT */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-black" />
        {CASE_STUDIES.map((c, i) => (
          <div
            key={c.brand}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-out ${
              i === active && !isAnimating ? "opacity-30" : "opacity-0"
            }`}
          >
            <img
              src={c.image}
              alt=""
              className="w-full h-full object-cover grayscale"
            />
            <div className="absolute inset-0 bg-black/65" />
          </div>
        ))}
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[78vh]">
        {/* LEFT: label + heading + arrows + counter */}
        <div className={`lg:col-span-5 reveal ${shown ? "in" : ""}`}>
          <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-8">
            Featured Case Studies
          </p>
          <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] max-w-md">
            Discover our latest and greatest tailor-made solutions for our partners.
          </h3>

          {/* Arrows */}
          <div className="mt-12 flex items-center gap-4">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous case study"
              className="group w-14 h-14 rounded-full border border-white/30 hover:border-white inline-flex items-center justify-center transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 transition-transform duration-200 group-hover:-translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next case study"
              className="group w-14 h-14 rounded-full border border-white/30 hover:border-white inline-flex items-center justify-center transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>

            <div className="ml-6 text-[11px] uppercase tracking-[0.28em] text-white/55 tabular-nums">
              <span className="text-white font-semibold">
                {String(active + 1).padStart(2, "0")}
              </span>
              <span className="mx-3 text-white/35">/</span>
              <span>{String(total).padStart(2, "0")}</span>
            </div>
          </div>

          {/* Meta strip */}
          <div
            className={`mt-10 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.24em] transition-opacity duration-300 ${
              isAnimating ? "opacity-0" : "opacity-100"
            }`}
          >
            <span className="text-white/85">{current.city}</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span className="text-white/65">{current.type}</span>
          </div>
        </div>

        {/* RIGHT: massive brand name + image card */}
        <div className="lg:col-span-7 relative">
          {/* Image card behind the type */}
          <div className="relative aspect-[4/5] md:aspect-[5/4] w-full overflow-hidden">
            {CASE_STUDIES.map((c, i) => (
              <img
                key={c.brand}
                src={c.image}
                alt={c.brand}
                className={`absolute inset-0 w-full h-full object-cover grayscale transition-all duration-[900ms] ease-out ${
                  i === active && !isAnimating
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-105"
                }`}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </div>

          {/* Brand display text — contained within card */}
          <div className="pointer-events-none absolute inset-0 flex items-end overflow-hidden">
            <div
              className={`px-6 md:px-10 pb-8 md:pb-12 w-full transition-all duration-500 ease-out ${
                isAnimating
                  ? "opacity-0 translate-y-3"
                  : "opacity-100 translate-y-0"
              }`}
            >
              <div className="font-display uppercase leading-[0.9] tracking-tight text-[8vw] sm:text-[7vw] md:text-[5.5vw] lg:text-[4.4vw] xl:text-[4.2vw]">
                {current.brand}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-12">
        <div className="flex gap-1">
          {CASE_STUDIES.map((c, i) => (
            <button
              key={`progress-${c.brand}`}
              onClick={() => goTo(i)}
              aria-label={`Go to case ${i + 1}`}
              className={`h-[2px] flex-1 transition-all duration-300 ${
                i === active ? "bg-white" : "bg-white/20 hover:bg-white/45"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
